import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, IdCard, Car } from "lucide-react";
import SEO from "../../components/SEO";
import { MEDIA, REGISTRATION_LINKS } from "../../lib/constants";

export default function VisitingDWCGeorgiaAve() {
  return (
    <>
      <SEO
        title="Visiting District Wellness Center: What to Expect at 4915 Georgia Ave NW"
        description="A first-timer's guide to walking into District Wellness Center at 4915 Georgia Ave NW, Washington DC. Parking, ID rules, hours, what happens at the door, and how to get in and out in 10 minutes."
        path="/blog/visiting-district-wellness-center-dc"
        ogType="article"
        ogImage={MEDIA.storefrontDoor}
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "Visiting DWC", path: "/blog/visiting-district-wellness-center-dc" },
        ]}
        article={{
          publishedTime: "2026-09-15",
          modifiedTime: "2026-09-15",
          section: "Visitor Guide",
          image: MEDIA.storefrontDoor,
        }}
      />

      {/* HERO */}
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">
              First-Time Visitor Guide
            </div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Walking into <em className="text-brand">DWC</em>.
            </h1>
            <p className="mt-6 text-ink-muted text-base sm:text-lg max-w-xl leading-relaxed">
              4915 Georgia Ave NW — Petworth&rsquo;s neighborhood dispensary.
              If it&rsquo;s your first time buying medical cannabis in DC, or
              your first time at District Wellness Center specifically,
              here&rsquo;s exactly what happens from the sidewalk to the
              register.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/locations"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="visit-cta-directions"
              >
                Directions & hours <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 border border-ink/15 text-ink hover:border-brand hover:text-brand px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
                data-testid="visit-cta-menu"
              >
                Pre-order from the menu <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <img
              src={MEDIA.storefrontDoor}
              alt="Customer opening the front door of District Wellness Center at 4915 Georgia Ave NW, Washington DC"
              className="w-full h-[520px] object-cover border border-black/10"
              loading="lazy"
              data-testid="visit-hero-image"
            />
          </div>
        </div>
      </section>

      {/* ARTICLE */}
      <article className="py-20 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 font-sans text-ink-muted">
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-0 mb-5">
            The vibe (in one paragraph)
          </h2>
          <p className="text-lg leading-relaxed">
            District Wellness Center sits on a bright stretch of Georgia Ave NW,
            just north of the Petworth Metro. From the outside it looks like a
            small, well-lit boutique — because that&rsquo;s what it is. Inside:
            a clean waiting lounge, a black-and-orange feature wall with the
            DWC monogram, plants, seating, and a check-in counter that gets
            you back to the retail floor quickly. It doesn&rsquo;t feel like a
            &ldquo;head shop.&rdquo; It feels like a neighborhood wellness
            store.
          </p>

          {/* Quick facts */}
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            <div className="p-6 bg-white border border-black/10">
              <MapPin size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Address</div>
              <div className="font-serif text-lg text-ink leading-tight">
                4915 Georgia Ave NW<br />Washington, DC 20011
              </div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <Clock size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Hours</div>
              <div className="text-sm text-ink leading-relaxed">
                Mon–Thu 8am–10pm<br/>Fri–Sat 8am–11pm<br/>Sun 9am–9pm
              </div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <IdCard size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">To Enter</div>
              <div className="text-sm text-ink leading-relaxed">
                21+ with valid photo ID · medical cannabis registration
              </div>
            </div>
            <div className="p-6 bg-white border border-black/10">
              <Car size={20} className="text-brand mb-3" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-1">Getting there</div>
              <div className="text-sm text-ink leading-relaxed">
                5 min walk from Petworth Metro (Green/Yellow) · street parking on Georgia Ave
              </div>
            </div>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Step 1 · At the door
          </h2>
          <p className="text-lg leading-relaxed">
            You&rsquo;ll walk up to the black-framed glass door at 4915. A
            greeter or patient specialist meets you inside the entryway and
            checks two things: your <strong>21+ photo ID</strong> and your
            <strong> medical cannabis registration</strong>. Both need to
            match. If you&rsquo;re not registered yet, the front team can
            hand you the myABCA link — it&rsquo;s about a 5-minute online
            process you can complete right there on your phone.
          </p>
          <div className="mt-6">
            <a
              href={REGISTRATION_LINKS.medical}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="visit-register-cta"
            >
              Register for Medical Cannabis <ArrowUpRight size={14} />
            </a>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Step 2 · The lounge
          </h2>
          <p className="text-lg leading-relaxed">
            If it&rsquo;s busy, you&rsquo;ll take a seat in the waiting lounge
            for a few minutes. The QR-code stand on the counter has a shortcut
            to our live Flowhub menu — a lot of first-timers use that time to
            skim what&rsquo;s in stock, so they walk into the retail floor
            with a shortlist.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Step 3 · The retail floor
          </h2>
          <p className="text-lg leading-relaxed">
            One patient at a time gets called back. A patient specialist walks
            you through flower jars (you can smell them), pre-rolls, vapes,
            edibles, tinctures and concentrates. Tell them what you&rsquo;re
            trying to <em>feel</em> — sleep, focus, pain, social — and
            they&rsquo;ll match products, not just push the highest-THC
            number. If you want to see something specific, ask. If you want
            to be left alone to browse, say that too — either is fine.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Step 4 · Checkout & out
          </h2>
          <p className="text-lg leading-relaxed">
            Cash and compliant digital payment options are both accepted;
            there&rsquo;s an ATM on-site. Product goes into a plain,
            child-resistant exit bag. If you pre-ordered on Flowhub, the whole
            visit — door to door — is usually under 10 minutes.
          </p>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            First-time tips
          </h2>
          <ul className="space-y-3 text-lg">
            <li className="flex gap-3">
              <span className="text-brand font-serif text-lg leading-none">✦</span>
              <span>Register on myABCA <em>before</em> you head over — it saves 5 minutes at the door.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-brand font-serif text-lg leading-none">✦</span>
              <span>New to edibles? Start at 2.5–5mg. Ask us if you&rsquo;re unsure.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-brand font-serif text-lg leading-none">✦</span>
              <span>New customers get a free pre-roll — just mention it at the register.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-brand font-serif text-lg leading-none">✦</span>
              <span>Same-day DC delivery is available if you&rsquo;d rather stay in.</span>
            </li>
          </ul>

          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-14 mb-5">
            Neighborhood essentials
          </h2>
          <p className="text-lg leading-relaxed">
            DWC sits between Petworth, Park View and Columbia Heights.
            Georgia Ave has plenty of parking, and the store is a short walk
            from Sherman Circle, Upshur Street, and the Petworth Recreation
            Center. If you&rsquo;re coming by Metro, exit Petworth station and
            walk north — you&rsquo;ll see the orange-and-black DWC sign in the
            window from a block away.
          </p>
        </div>
      </article>

      {/* FINAL CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            We&rsquo;d love to meet you.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Come by 4915 Georgia Ave NW any day of the week — or reserve
            online and pick up when you&rsquo;re ready.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="visit-final-directions"
            >
              Get directions <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-brand hover:text-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="visit-final-menu"
            >
              Browse the menu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
